// Texts of the daily-use screens (tabs Maschine, Bezug, Einstellungen, Wartung), German wording of the local app
// (frontend-orione/index.html, T.de) so Dominik finds the same words, English alongside.
import type { Lang } from './i18n.js';

export const DAILY_TEXTS = {
  tab_status: { de: 'Maschine', en: 'Machine' },
  tab_brew: { de: 'Bezug', en: 'Brew' },
  tab_settings: { de: 'Einstellungen', en: 'Settings' },
  tab_care: { de: 'Wartung', en: 'Care' },

  // header chip
  chip_offline: { de: 'Offline', en: 'Offline' },
  chip_start: { de: 'Startet', en: 'Starting' },
  chip_ready: { de: 'Bereit', en: 'Ready' },
  chip_heating: { de: 'Heizt auf', en: 'Heating up' },
  chip_cooling: { de: 'Kühlt ab', en: 'Cooling down' },
  chip_brew: { de: 'Bezug {s} s', en: 'Brewing {s} s' },
  chip_pi: { de: 'Pre-Infusion {s} s', en: 'Pre-infusion {s} s' },
  chip_flush_s: { de: 'Spülen {s} s', en: 'Flushing {s} s' },
  chip_flush: { de: 'Spülen', en: 'Flushing' },
  chip_flush_n: { de: 'Spülen {n}/{m}', en: 'Flushing {n}/{m}' },
  chip_steam: { de: 'Dampf', en: 'Steam' },
  chip_steam_cool: { de: 'Abkühlen nach Dampf', en: 'Cooling after steam' },
  chip_water: { de: 'Heißwasser', en: 'Hot water' },
  chip_backflush: { de: 'Backflush', en: 'Backflush' },
  chip_backflush_n: { de: 'Backflush {c}/{n}', en: 'Backflush {c}/{n}' },
  chip_pid_off: { de: 'Regelung aus', en: 'Control off' },
  chip_tank: { de: 'Wassertank leer', en: 'Water tank empty' },
  chip_standby: { de: 'Standby', en: 'Standby' },
  chip_overtemp: { de: 'Übertemperatur', en: 'Too hot' },
  chip_sensor: { de: 'Sensorfehler', en: 'Sensor error' },
  chip_descale: { de: 'Entkalken', en: 'Descaling' },
  chip_descale_round: { de: 'Entkalken {r}/8', en: 'Descaling {r}/8' },
  chip_unknown: { de: '…', en: '…' },
  toStandby: { de: 'Standby', en: 'Standby' },
  wakeUp: { de: 'Aufwecken', en: 'Wake up' },
  // why nothing can be changed right now, and when that changes (19.10.2026: "nur lesbar" was unclear)
  readOnlyOffline: {
    de: 'Maschine ist offline. Zuletzt gesehen {ago}.',
    en: 'Machine is offline. Last seen {ago}.',
  },
  readOnlyLink: {
    de: 'Keine Verbindung zur Cloud. Stand von {t}.',
    en: 'No connection to the cloud. Last update at {t}.',
  },
  readOnlyRestarting: {
    de: 'Maschine startet neu.',
    en: 'Machine is restarting.',
  },
  readOnlyUnknown: {
    de: 'Maschine hat sich noch nicht gemeldet.',
    en: 'Machine hasn’t checked in yet.',
  },
  readOnlyRevoked: {
    de: 'Zugang wurde beendet. Bitte neue Einladung beim Besitzer anfragen.',
    en: 'Access has ended. Please ask the owner for a new invitation.',
  },
  notSentLink: {
    de: 'Nicht gesendet. Keine Verbindung zur Cloud.',
    en: 'Not sent. No connection to the cloud.',
  },
  restartBar: { de: 'Wirkt nach einem Neustart', en: 'Takes effect after a restart' },
  restartQ: { de: 'Maschine neu starten?', en: 'Restart the machine?' },
  restartText: {
    de: 'Maschine ist in ~1 min wieder da. Ein laufender Bezug bricht ab.',
    en: 'Machine is back in ~1 min. A running shot stops.',
  },
  // messages after a command: success is quiet (19.10.2026), failures say what happened and what to do
  failedToast: { de: 'Nicht gespeichert', en: 'Not saved' },
  failedActionToast: { de: 'Nicht ausgeführt', en: 'Didn’t go through' },
  savedAfterBrew: { de: 'Gespeichert, wirkt nach dem Bezug', en: 'Saved, takes effect after the shot' },
  savedAfterRestart: {
    de: 'Gespeichert, wirkt nach einem Neustart',
    en: 'Saved, takes effect after a restart',
  },
  notConfirmed: {
    de: 'Nicht bestätigt. Verbindung ist abgebrochen.',
    en: 'Not confirmed. Connection dropped.',
  },
  noResponse: {
    de: 'Maschine hat nicht geantwortet.',
    en: 'Machine didn’t respond.',
  },
  notSentMachine: {
    de: 'Nicht gesendet. Maschine ist nicht erreichbar.',
    en: 'Not sent. Machine can’t be reached.',
  },
  expiredToast: {
    de: 'Kam zu spät bei der Maschine an. Bitte noch einmal versuchen.',
    en: 'Reached the machine too late. Please try again.',
  },
  staleToast: {
    de: 'Nicht ausgeführt. Zustand der Maschine hat sich geändert.',
    en: 'Didn’t go through. Machine state has changed.',
  },
  dismiss: { de: 'Ausblenden', en: 'Dismiss' },
  // the (i) after a title that opens its explanation
  infoShow: { de: 'Erklärung anzeigen', en: 'Show explanation' },
  freshLogin: {
    de: 'Letzte Anmeldung ist älter als 15 Minuten. Bitte neu anmelden.',
    en: 'Last sign-in was over 15 minutes ago. Please sign in again.',
  },
  ownerOnly: { de: 'Nur der Besitzer kann das ändern', en: 'Only the owner can change this' },
  // once over a page a member can't change (OwnerNote), a whole sentence (WRITING.md 4)
  ownerOnlyNote: { de: 'Nur der Besitzer kann das ändern.', en: 'Only the owner can change this.' },
  yes: { de: 'Ja', en: 'Yes' },
  ok: { de: 'OK', en: 'OK' },
  less: { de: 'weniger', en: 'less' },
  more: { de: 'mehr', en: 'more' },
  on: { de: 'an', en: 'on' },
  off: { de: 'aus', en: 'off' },
  zeroOff: { de: 'aus', en: 'off' },
  zeroNever: { de: 'nie', en: 'never' },
  displayAlways: { de: 'immer an', en: 'always on' },
  today: { de: 'heute', en: 'today' },
  yesterday: { de: 'gestern', en: 'yesterday' },
  tomorrow: { de: 'morgen', en: 'tomorrow' },
  fine: { de: 'Feineinstellung', en: 'Fine tuning' },

  // machine tab
  heater: { de: 'Heizung', en: 'Heater' },
  pid: { de: 'Heizen', en: 'Heating' },
  setpointShort: { de: 'Soll', en: 'Target' },
  heatOn: { de: 'an', en: 'on' },
  heatOnStandby: { de: 'an, Standby in {t}', en: 'on, standby in {t}' },
  heatStandby: { de: 'Standby, wecken mit dem Bezugsschalter', en: 'Standby, wake with the brew switch' },
  // under "STANDBY" in the ring: the state is already there (23.10.2026: "Standby" twice, the line touched the ring)
  heatStandbyWarm: { de: 'Warm {c} °C, aus in {t}', en: 'Warm {c} °C, off in {t}' },
  heatOff: { de: 'aus', en: 'off' },
  // the heating row of the machine tab (no switch there since 19.10.2026)
  standbyIn: { de: 'Standby in {t}', en: 'Standby in {t}' },
  heatOffHome: { de: 'Regelung aus', en: 'Temperature control off' },
  pidEnabled: { de: 'Regelung', en: 'Temperature control' },
  // behind the (i) of "Regelung" (board "Fortgeschritten"); whole sentences (23.10.2026: it read like a telegram)
  pidEnabledInfo: {
    de: 'Aus schaltet die Heizung ganz ab. Für den Alltag ist Standby gedacht.',
    en: 'Off turns the heater off completely. Standby is meant for daily use.',
  },
  alarmOver: { de: 'Maschine ist zu heiß.', en: 'Machine is too hot.' },
  alarmOverHint: {
    de: 'Heizung bleibt aus bis zum Abkühlen.',
    en: 'Heater stays off until it cools down.',
  },
  alarmSensor: { de: 'Temperaturfühler meldet Fehler.', en: 'Temperature sensor reports an error.' },
  alarmSensorHint: {
    de: 'Heizung ist aus. Bitte Fühler und Kabel prüfen.',
    en: 'Heater off. Please check the sensor and cable.',
  },
  alarmTank: { de: 'Wassertank ist leer.', en: 'Water tank is empty.' },
  alarmTankHint: { de: 'Bitte auffüllen.', en: 'Please refill.' },
  steamHot: { de: 'Zu heiß für Espresso.', en: 'Too hot for espresso.' },
  steamHotHint: {
    de: 'Bitte ohne Siebträger spülen.',
    en: 'Please flush without the portafilter.',
  },
  bfActive: {
    de: 'Backflush bereit. Bitte Bezugsschalter einschalten.',
    en: 'Backflush ready. Please turn on the brew switch.',
  },
  bfActiveBy: {
    de: '{by} hat den Backflush vorbereitet. Bitte Bezugsschalter einschalten.',
    en: '{by} set up the backflush. Please turn on the brew switch.',
  },
  clean1: {
    de: 'Reinigung bereit. Bitte Reiniger ins Blindsieb geben und Bezugsschalter einschalten.',
    en: 'Cleaning ready. Add cleaner, then turn on the brew switch.',
  },
  clean2: {
    de: 'Bitte Blindsieb ausspülen. Dann Bezugsschalter aus- und wieder einschalten.',
    en: 'Please wash out the blind basket. Then turn the brew switch off and on.',
  },
  clean3: { de: 'Klarspülen mit Wasser läuft.', en: 'Rinsing with water.' },
  bfProgress: { de: 'Backflush: Zyklus {c} von {n} · {p}', en: 'Backflush: cycle {c} of {n} · {p}' },
  bfPumping: { de: 'Pumpen', en: 'pumping' },
  bfPause: { de: 'Pause', en: 'pause' },
  bfDoneNote: {
    de: 'Backflush fertig. Bitte Bezugsschalter ausschalten, Blindsieb herausnehmen, kurz spülen.',
    en: 'Backflush done. Please switch off, remove the basket and flush.',
  },
  bfDue: {
    de: 'Backflush fällig nach {n} Bezügen. Bitte unter Wartung starten.',
    en: 'Backflush due after {n} shots. Please start it in Care.',
  },
  descaleDue: {
    de: 'Entkalken fällig nach ~{l} l. Bitte unter Wartung starten.',
    en: 'Descaling due after ~{l} l. Please start it in Care.',
  },
  dripDue: {
    de: 'Tropfschale ist fast voll, ~{ml} ml.',
    en: 'Drip tray is nearly full, ~{ml} ml.',
  },
  dripEmptied: { de: 'Als geleert markieren', en: 'Mark as emptied' },
  dripDueTitle: { de: 'Tropfschale fast voll', en: 'Drip tray almost full' },
  // under the ring once a shot is done (prototype "Bezug fertig")
  shotDoneLine: { de: 'Espresso ist fertig.', en: 'Espresso is ready.' },
  shotDoneIn: { de: 'in {s} s', en: 'in {s} s' },
  bootCrash: {
    de: 'Maschine ist abgestürzt. Neustart vor {m} min.',
    en: 'Machine crashed. Restarted {m} min ago.',
  },
  flushNote: {
    de: 'Spült 3 × kurz. Bitte Tasse unterstellen.',
    en: 'About to flush 3 times. Please place a cup underneath.',
  },
  // the warm-up flush by itself waits while the drip tray is reported full (firmware OrioneWarmupFlush.h, 25.10.2026)
  flushWaitsDrip: {
    de: 'Spült erst nach dem Leeren der Tropfschale.',
    en: 'Flushes once the drip tray is emptied.',
  },
  rinseNote: {
    de: 'Zeit zum Spülen. Bitte Siebträger herausnehmen.',
    en: 'Time to flush. Please remove the portafilter.',
  },
  flushNow: { de: 'Jetzt spülen', en: 'Flush now' },
  rinsing: { de: 'Spült …', en: 'Flushing …' },
  lastShot: { de: 'Letzter Bezug', en: 'Last shot' },
  cTodayN: { de: '{n} heute', en: '{n} today' },
  cWeekN: { de: '{n} diese Woche', en: '{n} this week' },
  careCard: { de: 'Pflege', en: 'Care' },
  careBf: { de: 'Backflush', en: 'Backflush' },
  careDescale: { de: 'Entkalken', en: 'Descaling' },
  careDrip: { de: 'Tropfschale', en: 'Drip tray' },
  careLeftShots: { de: 'in {n} Bezügen', en: 'in {n} shots' },
  careLeftL: { de: 'in ~{l} l', en: 'in ~{l} l' },
  history: { de: 'Letzte 20 Minuten', en: 'Last 20 minutes' },
  historyEmpty: { de: 'Seit dem Neustart noch kein Verlauf.', en: 'No history yet since the restart.' },
  historyWaiting: { de: 'Verlauf lädt …', en: 'Loading history …' },
  tempLine: { de: 'Temperatur', en: 'Temperature' },
  // the curves of a shot with their unit (the owner, 20.10.2026: the unit at each coloured line, and the most of it)
  legendWeight: { de: 'Gewicht (g)', en: 'Weight (g)' },
  shotCurveTitle: { de: 'Kurve', en: 'Curve' },
  legendFlow: { de: 'Durchfluss (g/s)', en: 'Flow (g/s)' },
  legendTemp: { de: 'Temperatur (°C)', en: 'Temperature (°C)' },
  setLine: { de: 'Soll', en: 'Target' },
  heatLine: { de: 'Heizung', en: 'Heater' },
  scale: { de: 'Waage', en: 'Scale' },
  scaleGone: { de: 'Waage getrennt', en: 'Scale disconnected' },
  holidayPill: { de: 'Urlaub bis {d}', en: 'Holiday until {d}' },
  schedNext: { de: 'Ein {d} {t}', en: 'On {d} {t}' },
  wifi: { de: 'WLAN', en: 'Wi-Fi' },
  wifiGood: { de: 'gut', en: 'good' },
  wifiOk: { de: 'mittel', en: 'fair' },
  wifiWeak: { de: 'schwach', en: 'weak' },

  // live shot
  dialBrew: { de: 'BEZUG', en: 'BREW' },
  dialDone: { de: 'FERTIG', en: 'DONE' },
  dialPi: { de: 'PRE-INFUSION', en: 'PRE-INFUSION' },
  // the ring while water runs without a shot (core/screens/activity.ts), as the round display
  dialFlush: { de: 'SPÜLEN', en: 'FLUSH' },
  dialBackflush: { de: 'BACKFLUSH', en: 'BACKFLUSH' },
  dialDescale: { de: 'ENTKALKEN', en: 'DESCALING' },
  flushNextIn: { de: 'Nächster in {s} s', en: 'Next in {s} s' },
  ringPumping: { de: 'Pumpt {t}', en: 'Pumping {t}' },
  ringSoaking: { de: 'Wirkt ein {t}', en: 'Soaking {t}' },
  dsCoolingShort: { de: 'Kühlt ab', en: 'Cooling down' },
  dialScaleLost: { de: 'Waage getrennt', en: 'Scale disconnected' },
  dialBfFill: { de: 'Füllen', en: 'Filling' },
  dialBfFlush: { de: 'Spülen', en: 'Flushing' },
  dialLeft: { de: 'Noch ~{s} s', en: '~{s} s left' },
  dialCleaner: { de: 'Mit Reiniger', en: 'With cleaner' },
  dialClearWater: { de: 'Klares Wasser', en: 'Clear water' },
  dialSwitchOn: { de: 'Bezugsschalter einschalten', en: 'Turn on the brew switch' },
  dialDsRefill: { de: 'Entkalker nachfüllen', en: 'Refill the descaler' },
  dialDsRest: { de: 'Ruht', en: 'Resting' },
  dialDsFill: { de: 'Klares Wasser einfüllen', en: 'Fill in clear water' },
  dialDsRinse: { de: 'Klarspülen', en: 'Rinsing' },
  dialTank: { de: 'WASSERTANK LEER', en: 'WATER TANK EMPTY' },
  dialRefill: { de: 'Bitte auffüllen.', en: 'Please refill.' },
  goal: { de: 'Ziel', en: 'Target' },
  // what a shot was set to (shot meta from the firmware of 19.10.2026 on)
  shotSetpoint: { de: 'Soll', en: 'Setpoint' },
  shotProfile: { de: 'Profil', en: 'Profile' },
  shotStop: { de: 'Stopp', en: 'Stop' },
  atStop: { de: 'Pumpe aus bei', en: 'Pump off at' },
  switchBack: { de: 'Bitte Bezugsschalter ausschalten.', en: 'Please turn off the brew switch.' },
  stopHandShort: { de: 'Stopp von Hand', en: 'Stop by hand' },

  // brew tab
  sShot: { de: 'Nächster Bezug', en: 'Next shot' },
  // the small caps label over the coffee of the next shot: the chosen preset, or the bean
  pickChosen: { de: 'Ausgewählt', en: 'Selected' },
  pickBean: { de: 'Bohne', en: 'Bean' },
  recipeAdjust: { de: 'Anpassen', en: 'Adjust' },
  manual: { de: 'Manuell', en: 'Manual' },
  manualSub: { de: 'ohne Hilfen', en: 'no assists' },
  beans: { de: 'Bohne', en: 'Bean' },
  beanNone: { de: 'keine', en: 'none' },
  beanNewOpt: { de: '+ Neue Bohne …', en: '+ New bean …' },
  beanLong: {
    de: 'Name ist zu lang. Höchstens 40 Zeichen, Umlaute zählen doppelt.',
    en: 'Name is too long. Max 40 characters, umlauts count as two.',
  },
  beanExists: { de: 'Diese Bohne gibt es schon.', en: 'This bean already exists.' },
  roast: { de: 'Röstdatum', en: 'Roast date' },
  roastAge: { de: 'Tag {n} nach Röstung', en: 'day {n} after roasting' },
  roastNone: { de: '– eintragen', en: '– add' },
  dose: { de: 'Dosis', en: 'Dose' },
  grindLabel: { de: 'Mahlgrad', en: 'Grind size' },
  brewTemp: { de: 'Temperatur', en: 'Temperature' },
  sPreinf: { de: 'Pre-Infusion', en: 'Pre-infusion' },
  piOn: { de: '{b} s Stoß, {p} s Pause', en: '{b} s burst, {p} s pause' },
  piBurst: { de: 'Stoß', en: 'Burst' },
  piPause: { de: 'Pause', en: 'Pause' },
  stop: { de: 'Stoppen', en: 'Stop' },
  stopHand: { de: 'von Hand', en: 'by hand' },
  stopByTime: { de: 'nach Zeit', en: 'by time' },
  stopByWeight: { de: 'nach Gewicht', en: 'by weight' },
  stopHint: { de: 'Nach Gewicht nur mit Waage', en: 'By weight needs a scale' },
  stopMax: { de: 'Höchstens {n} s', en: 'At most {n} s' },
  targetTime: { de: 'Bezugszeit', en: 'Brew time' },
  afterPiInfo: { de: 'Die Zeit zählt nach der Pre-Infusion.', en: 'The time counts after the pre-infusion.' },
  targetWeight: { de: 'Zielgewicht', en: 'Target weight' },
  ratio: { de: 'Verhältnis', en: 'Ratio' },
  tempEnd: { de: 'Profil', en: 'Profile' },
  // board "Feineinstellung": the profile as its own card, the stop with its (i)
  tempProfile: { de: 'Temperaturverlauf', en: 'Temperature profile' },
  tempProfileInfo: {
    de: 'Die Solltemperatur ändert sich während des Bezugs gleichmäßig bis zu diesem Wert.',
    en: 'The target temperature changes evenly during the shot up to this value.',
  },
  tempEndRow: { de: 'Bis zum Ende', en: 'By the end' },
  tempFlat: {
    de: 'Temperatur bleibt den ganzen Bezug bei {t} °C.',
    en: 'Stays at {t} °C for the whole shot.',
  },
  tempRamp: { de: '{a} °C beim Start, {b} °C am Ende.', en: 'Starts at {a} °C, ends at {b} °C.' },
  lag: { de: 'Nachlaufzeit', en: 'Stop lag' },
  lagInfo: {
    de: 'Die Pumpe stoppt um diese Zeit vor dem Ziel. Nach dem Stopp läuft noch Kaffee nach. Der Wert wird mit jedem Bezug neu gelernt.',
    en: 'The pump stops this long before the target. Coffee keeps dripping after the stop. The value updates with every shot.',
  },
  lagLast: { de: '~{g} g bei {f} g/s', en: '~{g} g at {f} g/s' },
  lagAt: { de: '~{g} g bei {f} g/s', en: '~{g} g at {f} g/s' },
  fallbackInfo: {
    de: 'Ohne verbundene Waage stoppt ein Bezug nach Gewicht nach dieser Zeit.',
    en: 'Without a connected scale, a shot by weight stops after this time.',
  },
  fallbackTime: { de: 'Ohne Waage nach', en: 'Without scale after' },
  tare: { de: 'Tarieren', en: 'Tare' },
  scaleOn: { de: 'Waage', en: 'Scale' },
  scaleOff: {
    de: 'Waage ist nicht verbunden. Bitte einschalten.',
    en: 'Scale isn’t connected. Please turn it on.',
  },
  scaleNone: {
    de: 'Keine Waage gewählt. Bitte unter Einstellungen → Waage wählen.',
    en: 'No scale chosen. Please pick one in Settings → Scale.',
  },
  battery: { de: 'Akku', en: 'Battery' },
  batteryLow: { de: 'laden', en: 'charge it' },
  beanActive: { de: 'In der Mühle', en: 'In the grinder' },
  beanUse: { de: 'Wählen', en: 'Use' },
  beanEdit: { de: 'Bearbeiten', en: 'Edit' },
  beanDelete: { de: 'Bohne löschen', en: 'Delete bean' },
  beanAdd: { de: 'Neue Bohne', en: 'New bean' },
  grinder: { de: 'Mühle', en: 'Grinder' },
  sPresets: { de: 'Schnellwahl', en: 'Presets' },
  presetKeep: { de: 'nicht ändern', en: 'keep' },
  presetName: { de: 'Name', en: 'Name' },
  presetTemp: { de: 'Temperatur', en: 'Temperature' },
  presetTime: { de: 'Bezugszeit', en: 'Brew time' },
  presetRatio: { de: 'Verhältnis', en: 'Ratio' },
  presetTake: { de: 'Aktuelle Werte übernehmen', en: 'Use current values' },
  presetStop: { de: 'Stoppen', en: 'Stop' },
  presetStopInfo: {
    de: 'Nach Zeit gilt die Bezugszeit, nach Gewicht das Verhältnis. Die Art wählt das Rezept.',
    en: 'By time uses the brew time, by weight the ratio. The recipe sets which one.',
  },
  presetBeanInfo: {
    de: 'Ist eine Bohne gewählt, wechselt die Schnellwahl auch die Bohne. Bei „nicht ändern“ bleibt die aktuelle Bohne.',
    en: 'With a bean chosen, the preset switches the bean too. With “keep”, the current bean stays.',
  },
  presetStopsAt: { de: 'stoppt bei {g} g', en: 'stops at {g} g' },
  sLast: { de: 'Letzte Bezüge', en: 'Last shots' },
  noShots: { de: 'Noch keine Bezüge', en: 'No shots yet' },
  loadMore: { de: 'Mehr laden', en: 'Load more' },
  shotTime: { de: 'Zeit', en: 'Time' },
  shotWeight: { de: 'Gewicht', en: 'Weight' },
  coldStart: { de: 'kalt gestartet', en: 'started cold' },
  handStop: { de: 'von Hand gestoppt', en: 'stopped by hand' },
  fromApp: { de: 'aus der App', en: 'from the app' },
  firstDropAt: { de: 'Erster Tropfen', en: 'First drop' },
  startTemp: { de: 'Start bei', en: 'Started at' },
  pumpOff: { de: 'Pumpe aus', en: 'pump off' },
  drip: { de: 'Nachlauf', en: 'After-drip' },
  // the shot chart and its figures as in the local app (slice 6, 19.10.2026)
  piClosed: { de: 'Ventil zu', en: 'valve closed' },
  piAfter: { de: 'Bezug danach', en: 'Brew afterwards' },
  // how the pump stop came about: lead = stop lag × flow (the figures of an opened shot)
  figLead: { de: '{g} g früher, {s} s × {f} g/s', en: '{g} g early, {s} s × {f} g/s' },
  t10: { de: '10 g nach', en: '10 g after' },
  refShort: { de: 'Ref.', en: 'Ref.' },
  chNote: {
    de: 'Durchfluss sprang, vielleicht Kanalbildung.',
    en: 'Flow jumped, possibly channelling.',
  },
  handStopNote: { de: 'Von Hand gestoppt, vor dem Ziel.', en: 'Stopped by hand before the target.' },
  fasterRef: {
    de: '{s} s schneller als die Referenz. Bitte feiner mahlen.',
    en: '{s} s faster than the reference. Please grind finer.',
  },
  slowerRef: {
    de: '{s} s langsamer als die Referenz. Bitte gröber mahlen.',
    en: '{s} s slower than the reference. Please grind coarser.',
  },
  flowLine: { de: 'Durchfluss', en: 'Flow' },
  compareWith: { de: 'Vergleichen mit', en: 'Compare with' },
  refChip: { de: 'Referenz', en: 'Reference' },
  refSet: { de: 'Als Referenz merken', en: 'Use as reference' },
  refClear: { de: 'Referenz entfernen', en: 'Remove reference' },
  refSaved: { de: 'Als Referenz gespeichert', en: 'Saved as reference' },
  refCleared: { de: 'Referenz entfernt', en: 'Reference removed' },
  tasteQ: { de: 'Geschmack', en: 'Taste' },
  sour: { de: 'sauer', en: 'sour' },
  // the middle of sour and bitter (Dominik 21.10.2026: "passt" sounded too pragmatic)
  good: { de: 'ausgewogen', en: 'balanced' },
  bitter: { de: 'bitter', en: 'bitter' },
  note: { de: 'Notiz', en: 'Note' },
  notePh: { de: 'Was fiel auf?', en: 'What stood out?' },
  noteSave: { de: 'Notiz speichern', en: 'Save note' },
  // a shot off its target in the history's row (board "Verlauf")
  devOver: { de: '{v} über dem Ziel', en: '{v} over target' },
  devUnder: { de: '{v} unter dem Ziel', en: '{v} under target' },
  deleteShot: { de: 'Bezug löschen', en: 'Delete shot' },
  deleteShotQ: { de: 'Bezug löschen?', en: 'Delete the shot?' },
  reallyDelete: {
    de: 'Kurve und Notiz von {s} gehen verloren.',
    en: 'Curve and note from {s} will be lost.',
  },
  deleteGo: { de: 'Löschen', en: 'Delete' },
  deleted: { de: 'Gelöscht', en: 'Deleted' },
  noCurve: { de: 'Keine Kurve gespeichert', en: 'No curve saved' },

  // bean library (concept beans-and-shot-history, 19.10.2026)
  back: { de: 'Zurück', en: 'Back' },
  beansAll: { de: 'Alle', en: 'All' },
  beansOpenN: { de: '{n} offen', en: '{n} open' },
  grinderNone: { de: 'noch nicht eingetragen', en: 'not set yet' },
  grinderName: { de: 'Name', en: 'Name' },
  // board "Mühle": which way the numbers go, two segments (nothing chosen while it is not set)
  grinderHigher: { de: 'Eine höhere Zahl heißt', en: 'A higher number means' },
  grinderFiner: { de: 'feiner', en: 'finer' },
  grinderCoarser: { de: 'gröber', en: 'coarser' },
  recipeTitle: { de: 'Rezept', en: 'Recipe' },
  shotTitle: { de: 'Bezug', en: 'Shot' },
  shotFigures: { de: 'Kennwerte', en: 'Figures' },
  shotMissing: { de: 'Diesen Bezug gibt es nicht mehr.', en: 'This shot no longer exists.' },
  presetPick: { de: 'Schnellwahl bearbeiten', en: 'Preset to edit' },
  beansAllN: { de: 'Alle (+{n})', en: 'All (+{n})' },
  // slice 5: taste tags and the reference per bean (19.10.2026)
  tasteTags: { de: 'Stichwörter', en: 'Tags' },
  tagsAll: { de: 'Alle', en: 'All' },
  tagAuto: { de: 'erkannt', en: 'detected' },
  refSetFor: { de: 'Als Referenz merken', en: 'Use as reference' },
  beanReference: { de: 'Referenz', en: 'Reference' },
  beanReferenceOpen: { de: 'Referenz öffnen', en: 'Open reference' },
  beanReferenceInfo: {
    de: 'Neue Bezüge dieser Bohne werden mit der Referenz verglichen. Die Referenz wird an einem Bezug gewählt.',
    en: "New shots of this bean are compared with the reference. It's set on a shot.",
  },
  beansTitle: { de: 'Bohnen', en: 'Beans' },
  beansEmpty: { de: 'Noch keine Bohnen', en: 'No beans yet' },
  beanArchived: { de: 'archiviert', en: 'archived' },
  beanShotsLabel: { de: 'Bezüge', en: 'Shots' },
  beanShotOne: { de: '1 Bezug', en: '1 shot' },
  beanShots: { de: '{n} Bezüge', en: '{n} shots' },
  // the figures of the history's filter (prototype "Verlauf")
  avgTime: { de: 'Ø Zeit', en: 'Ø time' },
  avgWeight: { de: 'Ø Gewicht', en: 'Ø weight' },
  avgRatio: { de: 'Ø Verhältnis', en: 'Ø ratio' },
  beanVersionSince: { de: 'Version {n} · seit {d}', en: 'Version {n} · since {d}' },
  beanVersions: { de: 'Rezeptstände', en: 'Recipe versions' },
  beanVersionN: { de: 'Version {n}', en: 'Version {n}' },
  beanFirstVersion: { de: 'erster Stand', en: 'first version' },
  beanSameVersion: { de: 'unverändert', en: 'unchanged' },
  bags: { de: 'Packungen', en: 'Bags' },
  bagsNone: { de: 'Noch keine Packung', en: 'No bag yet' },
  bagRoast: { de: 'Röstung', en: 'Roasted' },
  bagEmptySince: { de: 'leer seit {d}', en: 'empty since {d}' },
  dayN: { de: 'Tag {n}', en: 'day {n}' },
  dayLabel: { de: 'Tag nach Röstung', en: 'Day after roasting' },
  beansOpen: { de: 'Aktiv {n}', en: 'Active {n}' },
  beansArchive: { de: 'Archiv {n}', en: 'Archive {n}' },
  beansArchiveEmpty: { de: 'Keine archivierten Bohnen', en: 'No archived beans' },
  beanArchive: { de: 'Archivieren', en: 'Archive' },
  beanUnarchive: { de: 'Zurückholen', en: 'Bring back' },
  beanDeleteQ: { de: 'Bohne löschen?', en: 'Delete the bean?' },
  reallyDeleteLibraryBean: {
    de: '„{n}“ wird mit Rezepten und Packungen gelöscht.',
    en: '“{n}” is deleted with its recipes and bags.',
  },
  beanEditTitle: { de: 'Bohne bearbeiten', en: 'Edit bean' },
  beanName: { de: 'Name', en: 'Name' },
  roaster: { de: 'Röster', en: 'Roaster' },
  origin: { de: 'Herkunft', en: 'Origin' },
  process: { de: 'Aufbereitung', en: 'Process' },
  moreDetails: { de: 'Mehr Angaben', en: 'More details' },
  openedDay: { de: 'Geöffnet am', en: 'Opened on' },
  bagWeight: { de: 'Gewicht', en: 'Weight' },
  // board "Neue Bohne": where the new bean's recipe comes from, as the row's value
  recipeFrom: { de: 'Rezept', en: 'Recipe' },
  recipeFromInfo: {
    de: 'Die neue Bohne startet mit diesen Werten. Danach hat jede Bohne ihr eigenes Rezept.',
    en: 'The new bean starts with these values. After that, each bean keeps its own recipe.',
  },
  recipeFromCurrent: { de: 'wie jetzt eingestellt', en: 'as set now' },
  recipeFromBean: { de: 'wie {n}', en: 'like {n}' },
  beanCreate: { de: 'Anlegen', en: 'Create' },
  beanCreateUse: { de: 'Anlegen und wählen', en: 'Create and use' },
  save: { de: 'Speichern', en: 'Save' },
  bagAdd: { de: 'Packung', en: 'Bag' },
  bagNew: { de: 'Neue Packung', en: 'New bag' },
  bagEmpty: { de: 'Leer', en: 'Empty' },
  bagEmptied: { de: 'Packung ist leer.', en: 'Bag is empty.' },
  bagAddGo: { de: 'Hinzufügen', en: 'Add' },
  bagLeftOf: { de: '~{g} von {w} g', en: '~{g} of {w} g' },
  undo: { de: 'Rückgängig', en: 'Undo' },

  // history with filters (FR-27) and the comparison
  shotHistory: { de: 'Verlauf', en: 'History' },
  period: { de: 'Zeitraum', en: 'Period' },
  periodToday: { de: 'Heute', en: 'Today' },
  period7: { de: '7 Tage', en: '7 days' },
  period30: { de: '30 Tage', en: '30 days' },
  periodAll: { de: 'Alle', en: 'All' },
  tasteAll: { de: 'Alle', en: 'All' },
  unrated: { de: 'unbewertet', en: 'not rated' },
  allBeans: { de: 'Alle Bohnen', en: 'All beans' },
  withoutBean: { de: 'ohne Bohne', en: 'without a bean' },
  historyNone: { de: 'Keine passenden Bezüge', en: 'No matching shots' },
  compare: { de: 'Vergleichen', en: 'Compare' },
  compareHint: { de: 'Zwei oder drei Bezüge antippen', en: 'Tap two or three shots' },
  compareChosen: { de: '{n} gewählt', en: '{n} chosen' },
  compareTitle: { de: 'Vergleich', en: 'Comparison' },
  compareLoading: { de: 'Kurven laden …', en: 'Loading curves …' },
  sPower: { de: 'Ein & Aus', en: 'On & off' },
  sWake: { de: 'Einschalten', en: 'Switching on' },
  scheduleTitle: { de: 'Zeitplan', en: 'Schedule' },
  planEmpty: { de: 'keine Zeiten', en: 'no times' },
  advancedHint: { de: 'Regelung, Offset, PID', en: 'Temperature control, offset, PID' },
  systemHint: { de: 'Firmware, Neustart, Diagnose', en: 'Firmware, restart, diagnostics' },
  claudeKeyYes: { de: 'Schlüssel gespeichert', en: 'Key saved' },
  claudeKeyNo: { de: 'kein Schlüssel', en: 'no key' },
  standbyEnabled: { de: 'Standby', en: 'Standby' },
  standbyEnabledHint: { de: 'Heizung aus nach längerer Pause', en: 'Turns the heater off when idle' },
  standbyTime: { de: 'Nach', en: 'After' },
  // under "Standby" and "Nach": short labels next to −/+ ("Danach ganz aus nach" said "nach" twice)
  warmTemp: { de: 'Warm halten auf', en: 'Keep warm at' },
  warmHours: { de: 'Ganz aus nach', en: 'Fully off after' },
  displayOff: { de: 'Uhr im Standby', en: 'Clock in standby' },
  displayOffHint: { de: 'Danach geht das Display aus', en: 'Then the display turns off' },
  warmupFlush: { de: 'Automatisch spülen', en: 'Flush automatically' },
  warmupFlushHint: { de: '3 Spülstöße nach einem Kaltstart', en: '3 flushes after a cold start' },
  warmupWait: { de: 'Wartezeit', en: 'Wait time' },
  // what the value is, not why (WRITING.md 0)
  warmupWaitHint: { de: 'Zeit am Soll vor dem Spülen', en: 'Time at target before flushing' },
  scheduleEnabled: { de: 'Automatisch einschalten', en: 'Switch on automatically' },
  scheduleEnabledHint: { de: 'Der Hauptschalter muss an bleiben', en: 'Leave the main switch on' },
  dayNames: { de: 'Mo,Di,Mi,Do,Fr,Sa,So', en: 'Mo,Tu,We,Th,Fr,Sa,Su' },
  dayNamesLong: {
    de: 'Montag,Dienstag,Mittwoch,Donnerstag,Freitag,Samstag,Sonntag',
    en: 'Monday,Tuesday,Wednesday,Thursday,Friday,Saturday,Sunday',
  },
  planNone: { de: 'aus', en: 'off' },
  // the week as a grid (prototype "Zeitplan"): a column per time of the day, each time its own field
  planWeek: { de: 'Woche', en: 'Week' },
  planSlot: { de: 'Zeit {n}', en: 'Time {n}' },
  planTapHint: { de: 'Zum Ändern eine Zeit antippen.', en: 'Tap a time to change it.' },
  planSlotTitle: { de: '{d}, Zeit {n}', en: '{d}, time {n}' },
  planSlotAdd: { de: '{d}, Zeit {n} hinzufügen', en: '{d}, add time {n}' },
  planReady: { de: 'Bereit um', en: 'Ready at' },
  planOffAt: { de: 'Ausschalten um', en: 'Switch off at' },
  planOffAtHint: { de: 'Ohne Ausschaltzeit gilt Standby', en: 'Without an off time, standby applies' },
  planAllDays: { de: 'Für alle Tage übernehmen', en: 'Use for all days' },
  planDelete: { de: 'Zeit löschen', en: 'Delete time' },
  wheelHour: { de: 'Stunde', en: 'Hour' },
  wheelMinute: { de: 'Minute', en: 'Minute' },
  planOnReady: { de: 'an und bereit', en: 'on and ready' },
  holiday: { de: 'Urlaub', en: 'Holiday' },
  holidayHint: { de: 'Der letzte Tag zählt noch zum Urlaub', en: 'The last day still counts as holiday' },
  holidayUntil: { de: 'pausiert bis {d}', en: 'paused until {d}' },
  holidayEnd: { de: 'Beenden', en: 'End' },
  zoneHint: {
    de: 'Zeiten gelten in der Zeitzone der Maschine, {z}.',
    en: 'Times are in the machine’s time zone, {z}.',
  },
  sScale: { de: 'Waage', en: 'Scale' },
  scaleEnabled: { de: 'Bluetooth-Waage', en: 'Bluetooth scale' },
  scaleEnabledInfo: { de: 'Wirkt nach einem Neustart.', en: 'Takes effect after a restart.' },
  autoTare: { de: 'Automatisch tarieren', en: 'Tare automatically' },
  autoTareHint: { de: 'Beim Start des Bezugs', en: 'When the shot starts' },
  learnLag: { de: 'Stopp automatisch lernen', en: 'Learn stop lag' },
  learnLagHint: { de: 'Aus jedem Bezug nach Gewicht', en: 'From every shot by weight' },
  scaleWith: { de: 'Verbunden mit {n}', en: 'Connected to {n}' },
  scaleWaiting: {
    de: '{n} · nicht verbunden',
    en: '{n} · not connected',
  },
  scaleNoneChosen: { de: 'Keine Waage gewählt', en: 'No scale chosen' },
  scaleDisabled: { de: 'Bluetooth-Waage aus', en: 'Bluetooth scale off' },
  scaleFind: { de: 'Suchen', en: 'Search' },
  scaleSearching: { de: 'Suche …', en: 'Searching …' },
  scaleConnect: { de: 'Verbinden', en: 'Connect' },
  scaleForget: { de: 'Vergessen', en: 'Forget' },
  scaleHint: {
    de: 'Bitte Waage einschalten und vom Handy trennen',
    en: 'Please turn on the scale and unpair it',
  },
  scaleNoneFound: {
    de: 'Keine Waage gefunden. Bitte einschalten und vom Handy trennen.',
    en: 'No scale found. Please turn it on and disconnect the phone.',
  },
  signalWeak: { de: 'Signal schwach', en: 'weak signal' },
  signalOk: { de: 'Signal mittel', en: 'medium signal' },
  signalGood: { de: 'Signal gut', en: 'good signal' },
  sClaude: { de: 'Claude', en: 'Claude' },
  sTank: { de: 'Wassertank', en: 'Water tank' },
  tankSensor: { de: 'Füllstandssensor', en: 'Level sensor' },
  tankSensorInfo: {
    de: 'Automatisch spülen und das Entkalk-Programm laufen nur mit Sensor. Wirkt nach einem Neustart.',
    en: 'Flushing automatically and the descaling programme need the sensor. Takes effect after a restart.',
  },
  tankMode: { de: 'Schaltart', en: 'Switch type' },
  tankModeInfo: {
    de: 'Tank voll, aber die App zeigt „leer“? Bitte die andere Schaltart wählen.',
    en: 'Full tank, but the app shows “empty”? Please choose the other switch type.',
  },
  tankNow: { de: 'Tank jetzt', en: 'Tank now' },
  tankWater: { de: 'Wasser da', en: 'has water' },
  tankEmpty: { de: 'leer', en: 'empty' },
  sDisplay: { de: 'Display', en: 'Display' },
  displayLanguage: { de: 'Sprache', en: 'Language' },
  // the word as the round display writes it in its language (board "Display")
  postBrew: { de: '„{w}“ zeigen', en: 'Show “{w}”' },
  postBrewInfo: {
    de: 'So lange zeigt das runde Display nach dem Bezug Zeit und Gewicht.',
    en: 'The round display shows time and weight this long after the shot.',
  },
  postBrewRow: { de: '„{w}“ {s}', en: '“{w}” {s}' },
  sPid: { de: 'Fortgeschritten', en: 'Advanced' },
  tempOffset: { de: 'Offset', en: 'Offset' },
  tempOffsetInfo: {
    de: 'Unterschied zwischen dem Fühler am Thermoblock und dem Wasser an der Gruppe.',
    en: 'Difference between the thermoblock sensor and the water at the group.',
  },
  readyBand: { de: 'Bereit ab', en: 'Ready within' },
  readyBandInfo: {
    de: 'So nah am Soll gilt die Maschine als bereit.',
    en: 'This close to the target, the machine counts as ready.',
  },
  heatBoost: { de: 'Heizen beim Bezug', en: 'Shot heating' },
  heatBoostInfo: {
    de: 'Mindestleistung der Heizung ab der Pre-Infusion. So bricht die Temperatur nicht ein.',
    en: 'Minimum heater power from the pre-infusion on. This keeps the temperature from dropping.',
  },
  emaHint: { de: 'Glättung', en: 'Smoothing' },
  emaInfo: {
    de: 'Glättet den Messwert des Fühlers. Höher heißt ruhiger, aber träger.',
    en: 'Smooths the sensor reading. Higher is calmer but slower.',
  },
  ponmInfo: { de: 'Weniger Überschwingen beim Aufheizen.', en: 'Less overshoot when heating up.' },
  sHeating: { de: 'Heizung', en: 'Heating' },
  sPidValues: { de: 'PID', en: 'PID' },
  pidInfo: {
    de: 'Die Werte der Temperaturregelung. Bitte nur in kleinen Schritten ändern. Danach ein paar Aufheizvorgänge beobachten.',
    en: 'The values of the temperature control. Please change them in small steps. Then watch a few heat-ups.',
  },
  sSystem: { de: 'System', en: 'System' },
  hostname: { de: 'Gerätename', en: 'Device name' },
  hostnameInfo: {
    de: 'Unter diesem Namen ist die Maschine im WLAN erreichbar.',
    en: 'The machine is found by this name on the Wi-Fi.',
  },
  logLevel: { de: 'Protokoll', en: 'Log level' },
  logLevelInfo: {
    de: 'Wie viel die Maschine mitschreibt. Für die Fehlersuche TRACE, sonst INFO.',
    en: 'How much the machine writes down. TRACE for troubleshooting, INFO otherwise.',
  },
  sMachine: { de: 'Maschine', en: 'Machine' },
  roleOwner: { de: 'Besitzer', en: 'Owner' },
  roleMember: { de: 'Mitnutzer', en: 'Member' },
  // the role as a pill on the machine's tile
  tagOwner: { de: 'Besitzer', en: 'Owner' },
  tagMember: { de: 'Mitnutzer', en: 'Member' },
  unpairMachine: { de: 'Maschine entkoppeln', en: 'Unpair machine' },
  show: { de: 'Anzeigen', en: 'Show' },
  hide: { de: 'Ausblenden', en: 'Hide' },
  machineChoose: { de: 'Maschine wählen', en: 'Choose machine' },
  appLanguage: { de: 'Sprache der App', en: 'App language' },
  protectedS: { de: 'Nur Besitzer, frische Anmeldung', en: 'Owner only, fresh sign-in' },

  // care tab
  sFlush: { de: 'Spülen', en: 'Flush' },
  flushWhat: {
    de: 'Spült 3 × kurz zum Vorwärmen. Bitte leeren Siebträger drin lassen.',
    en: 'Flushes 3 times to warm up. Please leave the empty portafilter in.',
  },
  flushStop: { de: 'Spülen abbrechen', en: 'Stop flushing' },
  flushPulse: { de: 'Spülstoß {n} von {m}', en: 'Flush {n} of {m}' },
  flushPause: { de: 'Pause, gleich Spülstoß {n} von {m}', en: 'Pause, flush {n} of {m} next' },
  flushBusy: { de: 'Möglich, sobald die Maschine bereit ist.', en: 'Possible once the machine is ready.' },
  flushSwitch: {
    de: 'Bitte zuerst Bezugsschalter ausschalten.',
    en: 'Please turn off the brew switch first.',
  },
  flushNeedsSensor: {
    de: 'Nur mit Füllstandssensor möglich. Siehe Einstellungen → Wassertank.',
    en: 'Only with the level sensor. See Settings → Water tank.',
  },
  flushConfirm: {
    de: 'Bitte Siebträger leeren oder herausnehmen und Tasse unterstellen.',
    en: 'Please empty or remove the portafilter. Place a cup underneath.',
  },
  bfStartQ: { de: 'Backflush starten?', en: 'Start the backflush?' },
  cleanStartQ: { de: 'Reinigung starten?', en: 'Start cleaning?' },
  dsStartQ: { de: 'Entkalken starten?', en: 'Start descaling?' },
  dsStopQ: { de: 'Entkalken stoppen?', en: 'Stop descaling?' },
  descaledQ: { de: 'Als entkalkt eintragen?', en: 'Mark as descaled?' },
  cardSettings: { de: 'Einstellungen', en: 'Settings' },
  // the main page of Wartung (prototype): what is due, then the programmes with a play button each
  careNext: { de: 'Als Nächstes', en: 'Up next' },
  carePrograms: { de: 'Programme', en: 'Programmes' },
  bfRowHint: { de: 'Mit Blindsieb, {n} Runden', en: 'With the blind basket, {n} rounds' },
  cleanRow: { de: 'Backflush mit Reiniger', en: 'Backflush with cleaner' },

  sBf: { de: 'Backflush', en: 'Backflush' },
  bfSince: { de: 'Vor {n} Bezügen', en: '{n} shots ago' },
  bfStart: { de: 'Starten', en: 'Start' },
  bfStop: { de: 'Beenden', en: 'Stop' },
  bfConfirm: {
    de: 'Bitte Blindsieb einsetzen und Bezugsschalter einschalten.',
    en: 'Please insert the blind basket. Then turn on the brew switch.',
  },
  cleanStart: { de: 'Mit Reiniger', en: 'With cleaner' },
  cleanConfirm: {
    de: 'Bitte Blindsieb mit Reiniger einsetzen. Jeder Schritt startet mit dem Bezugsschalter.',
    en: 'Please insert the blind basket with cleaner. Each step starts with the brew switch.',
  },
  cleanStop: { de: 'Reinigung beenden', en: 'Stop cleaning' },
  bfCycles: { de: 'Runden', en: 'Rounds' },
  bfFill: { de: 'Pumpen', en: 'Pump' },
  bfFlush: { de: 'Pause', en: 'Pause' },
  // the reminders (board "Erinnerungen"): the thing it reminds of as the label, the unit under it
  careRemind: { de: 'Erinnern', en: 'Remind me' },
  careRemindInfo: {
    de: 'Backflush nach so vielen Bezügen, Entkalken nach so viel Wasser. Fällige Pflege steht in der Wartung unter „Als Nächstes“.',
    en: 'Backflush after this many shots, descaling after this much water. Anything due shows in Care under “Up next”.',
  },
  remindShots: { de: 'Bezüge', en: 'Shots' },
  remindWater: { de: 'Wasser', en: 'Water' },
  remindCapacity: { de: 'Volumen', en: 'Capacity' },
  dripCapacityInfo: {
    de: 'Bitte Schale einmal mit einem Messbecher ausmessen.',
    en: 'Please measure the tray once with a measuring jug.',
  },
  bfProgram: { de: 'Backflush-Programm', en: 'Backflush programme' },
  bfProgramInfo: {
    de: 'So läuft ein Backflush mit Blindsieb ab. Jede Runde pumpt und pausiert einmal.',
    en: 'A backflush with the blind basket runs like this. Each round pumps and pauses once.',
  },
  sDescale: { de: 'Entkalken', en: 'Descaling' },
  descaleSince: { de: '~{l} l seit {d}', en: '~{l} l since {d}' },
  descaleCounted: { de: '~{l} l bisher', en: '~{l} l so far' },
  dsStart: { de: 'Starten', en: 'Start' },
  dsNeedsSensor: {
    de: 'Nur mit Füllstandssensor möglich.',
    en: 'Only with the level sensor.',
  },
  dsConfirm: {
    de: 'Dauert ~1 Stunde. Bitte Entkalker einfüllen, Siebträger herausnehmen, großes Gefäß unterstellen.',
    en: 'Takes ~1 hour, needs a large container. Please add descaler and remove the portafilter.',
  },
  dsCooling: {
    de: 'Kühlt ab, jetzt {c} °C. Entkalken startet unter 60 °C.',
    en: 'Cooling down, now {c} °C. Descaling starts below 60 °C.',
  },
  dsRound: { de: 'Runde {r} von {n} · {what} · noch {t}', en: 'Round {r} of {n} · {what} · {t} left' },
  dsPumping: { de: 'pumpt', en: 'pumping' },
  dsSoaking: { de: 'wirkt ein', en: 'soaking' },
  dsRefill: {
    de: 'Tank ist leer. Bitte Entkalker nachfüllen.',
    en: 'Tank is empty. Please top up with descaler.',
  },
  dsRest: {
    de: 'Restlicher Entkalker läuft durch.',
    en: 'Remaining descaler is running through.',
  },
  dsWaitRinse: {
    de: 'Klarspülen {p} von 2. Bitte Tank ausspülen, mit Wasser füllen, Gefäß leeren.',
    en: 'Rinse {p} of 2. Please rinse, refill the tank, empty the container.',
  },
  dsRinse: {
    de: 'Klarspülen {p} von 2 läuft.',
    en: 'Rinse {p} of 2 running.',
  },
  dsNext: { de: 'Weiter', en: 'Next' },
  dsStop: { de: 'Stoppen', en: 'Stop' },
  dsReallyStop: {
    de: 'Pumpe stoppt, Heizung geht wieder an.',
    en: 'Pump stops, heater comes back on.',
  },
  descaled: { de: 'Schon entkalkt', en: 'Already descaled' },
  reallyDescaled: { de: 'Zähler beginnt wieder bei 0.', en: 'Counter goes back to 0.' },
  descaleHow: { de: 'Ohne Programm entkalken', en: 'Descale without the programme' },
  // board "Entkalken": the water since the last time, the programme in its steps
  dsSince: { de: 'Seit dem letzten Entkalken', en: 'Since the last descaling' },
  dsOf: { de: 'von {l} l', en: 'of {l} l' },
  dsLast: { de: 'zuletzt {d}', en: 'last {d}' },
  dsRemindLink: { de: 'Erinnerung', en: 'Reminder' },
  dsProgram: { de: 'Programm', en: 'Programme' },
  dsProgramInfo: {
    de: 'Nur mit Füllstandssensor möglich. Jeder weitere Schritt wird in der App bestätigt.',
    en: 'Only with the level sensor. Each further step is confirmed in the app.',
  },
  dsDuration: { de: '~1 Stunde', en: '~1 hour' },
  dsStep1: { de: 'Vorbereiten', en: 'Prepare' },
  dsStep1Text: {
    de: 'Entkalker einfüllen und Siebträger herausnehmen. Großes Gefäß unter die Gruppe stellen.',
    en: 'Add descaler and remove the portafilter. Put a large container under the group.',
  },
  dsStep2: { de: 'Abkühlen', en: 'Cool down' },
  dsStep2Text: { de: 'Entkalken startet unter 60 °C.', en: 'Descaling starts below 60 °C.' },
  dsStep3: { de: 'Entkalken', en: 'Descale' },
  dsStep3Text: {
    de: 'Pumpen und Einwirken, bis der Tank leer ist.',
    en: 'Pumping and soaking until the tank is empty.',
  },
  dsStep4: { de: 'Klarspülen', en: 'Rinse' },
  dsStep4Text: {
    de: 'Zwei Tanks klares Wasser durchlaufen lassen. Vorher jeweils das Gefäß leeren.',
    en: 'Run two tanks of fresh water through. Empty the container before each.',
  },
  dsStartProgram: { de: 'Programm starten', en: 'Start the programme' },
  descale1: {
    de: 'Entkalker nach Packungsangabe in den Tank geben.',
    en: 'Add descaler to the tank as directed.',
  },
  descale2: {
    de: 'Gefäß unter die Gruppe stellen. Halben Tank stoßweise mit dem Bezugsschalter durchlaufen lassen.',
    en: 'Put a container under the group. Pulse the brew switch for half a tank.',
  },
  descale3: {
    de: 'Rest durchlaufen lassen und Tank ausspülen.',
    en: 'Run the rest through, then rinse the tank.',
  },
  descale4: {
    de: 'Zwei Tanks klares Wasser durchlaufen lassen. Dann „Schon entkalkt“ antippen.',
    en: 'Run two tanks of fresh water through. Then tap “Already descaled”.',
  },
  descaleSrc: {
    de: 'Quelle: Quick-Mill-Nutzer im Kaffee-Netz. Nicht im Handbuch.',
    en: 'Source: Quick Mill owners on Kaffee-Netz. Not in the manual.',
  },
  sDrip: { de: 'Tropfschale', en: 'Drip tray' },
  dripNow: { de: '~{ml} von {cap} ml', en: '~{ml} of {cap} ml' },

  sCount: { de: 'Zähler', en: 'Counters' },
  cToday: { de: 'Heute', en: 'Today' },
  cWeek: { de: 'Woche', en: 'Week' },
  cTotal: { de: 'Gesamt', en: 'Total' },
  cCoffee: { de: 'Kaffee', en: 'Coffee' },
  sDevice: { de: 'Gerät', en: 'Device' },
  deviceHint: { de: 'Firmware {fw}', en: 'Firmware {fw}' },
  // labels of the device card (label and value)
  fwVersion: { de: 'Firmware', en: 'Firmware' },
  fwPending: { de: 'neue Firmware wird geprüft', en: 'new firmware on trial' },
  lastStart: { de: 'Letzter Start', en: 'Last start' },
  // why the machine last started, as the value of "Letzter Start" at the right of its row (board "Gerät": "nach einem
  // Update"; 23.10.2026: "Rückkehr zur vorigen Firmware" wrapped)
  cause_power_on: { de: 'Strom an', en: 'power on' },
  cause_user: { de: 'an der Maschine', en: 'at the machine' },
  cause_remote: { de: 'aus der App', en: 'from the app' },
  cause_update: { de: 'nach einem Update', en: 'after an update' },
  cause_rollback: { de: 'vorige Firmware', en: 'previous firmware' },
  cause_crash: { de: 'nach Absturz', en: 'after a crash' },
  cause_watchdog: { de: 'Software hing', en: 'software froze' },
  cause_heap_guard: { de: 'Speicher knapp', en: 'low memory' },
  cause_brownout: { de: 'Unterspannung', en: 'low voltage' },
  cause_controlled: { de: 'geplant', en: 'planned' },
  cause_unknown: { de: 'unbekannt', en: 'unknown' },
  restart: { de: 'Neu starten', en: 'Restart' },

  // the sheets of Einstellungen (23.10.2026): a title that stands alone ("Nach" was a fragment), the value under it
  standbyAfterTitle: { de: 'Standby nach', en: 'Standby after' },
  warmTempShort: { de: 'Temperatur', en: 'Temperature' },
  // an empty date field (iOS shows nothing in it)
  dateChoose: { de: 'Datum wählen', en: 'Choose a date' },
  holidayPause: { de: 'Pausieren', en: 'Pause' },
  schedEmpty: {
    de: 'Noch keine Zeiten. Bitte unten ein „+“ antippen.',
    en: 'No times yet. Please tap a “+” below.',
  },
  // the scale's page (board "Waage")
  scaleConnectedTag: { de: 'verbunden', en: 'connected' },
  scaleDisconnectedTag: { de: 'nicht verbunden', en: 'not connected' },
  scaleBatteryTag: { de: 'Akku {n} %', en: 'Battery {n} %' },
  scaleForgetLong: { de: 'Waage vergessen', en: 'Forget this scale' },
  scaleChoose: { de: 'Waage wählen', en: 'Choose a scale' },
  scaleChooseInfo: {
    de: 'Bitte Waage neben der Maschine einschalten. Verbunden wird nur die gewählte Waage.',
    en: 'Please turn on the scale near the machine. Only the chosen scale is connected.',
  },
} as const satisfies Record<string, Record<Lang, string>>;
